/* Built In Imports */
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useContext, useEffect } from 'react';

/* External Imports */
import { Box } from '@chakra-ui/react';

/* Internal Imports */
/* Components */
import config from '@config';
import WrapLayout from '@Layout/WrapLayout';
// import Breadcrum from '@components/Navigation/Breadcrum';
import ProgramDataUI from '@components/UI/ProgramDataUI';
import HeadComponent from '@components/Utility/HeadComponent';
import Loader from '@components/Utility/Loader';
import { AuthContext } from '@store/auth-context';

/* Services */
import { getLocale } from '@components/Utility/Shared/SharedService';
import {
  fetchFooterData,
  fetchHeaderMenuData,
  fetchHeaderTopMenuData,
} from 'services/commonService';
import { getLandingPaths } from 'services/othersService';

/* Styles */

/**
 * @param {string} region - Current User Region
 * @param {string} language - Current User Language
 * @param {string} pageData - Program Details Page Data
 */

const ProgramDetails = ({ region, language, pageData }) => {
  const router = useRouter();
  const { query, isReady } = router;
  const { id } = query;
  const authContext = useContext(AuthContext);

  useEffect(() => {
    authContext.RegionAndLang(region, language);
  }, [authContext, region, language]);

  // consoleLog('page', pageData);

  if (!isReady) {
    return <Loader pos="relative" />;
  }

  if (!id || isNaN(id) || id.length > 7) {
    return (
      <Box as="section" className="page-wrap container-article">
        <Box>
          <Box
            w={{ md: '632px', sm: '432px', base: '302px' }}
            minW="302px"
            maxW="632px"
            m="0 auto"
            d="grid"
            placeItems="center"
            // bg="#f4f4f4"
            p={{
              md: '51px 69px 57px 70px',
              sm: '45px 15px 54px',
              base: '45px 15px 54px',
            }}
            textAlign="center"
          >
            <Box
              as="p"
              pb="20px"
              fontSize={{ md: '18px', sm: '16px', base: '16px' }}
              fontFamily={'FedraSansStd-medium'}
              lineHeight={{ md: '1.56', sm: '1.5', base: '1.5' }}
              textAlign="center"
              color={'#464038'}
              className="userMessage"
            >
              <style global jsx>
                {`
                  .userMessage p {
                    line-height: 1.56;
                    letter-spacing: normal;
                    text-align: center;
                    color: #cf4520;
                  }
                `}
              </style>
              Oops! Something went wrong! This program is not found in our
              system. Help us improve your experience by sending an error
              report.
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <>
    <Head>
        <meta name="robots" content="noindex" />
    </Head>
    <WrapLayout>
      <HeadComponent
        data={pageData?._seoMetaTags || pageData?.seoFields}
        availableLocals={pageData?.availableLocales}
        pageconfig={pageData?.pageConfig}
      >
        {' '}
      </HeadComponent>
      <Box as="section" className="page-wrap container-article" p="0">
        {id && id.length > 0 ? (
          <Box>
            <ProgramDataUI
              key="ProgramDataUI"
              id={id}
              region={region}
              lang={language}
            />
          </Box>
        ) : (
          <Box></Box>
        )}
      </Box>
    </WrapLayout>
    </>
  );
};

export const getStaticPaths = async () => {
  const programDetailsLandingPaths = config.USE_LOCAL_URL
    ? [
        `/in/en/program-details`,
        `/au/en/program-details`,
        `/ca/en/program-details`,
        `/my/en/program-details`,
        `/sg/en/program-details`,
        `/eu/en/program-details`,
        `/us/en/program-details`,
        `/global/en/program-details`,
      ]
    : await getLandingPaths('lvl0', '/program-details');
  return {
    paths: programDetailsLandingPaths,
    fallback: false,
  };
};

export const getStaticProps = async ({
  preview,
  previewData,
  params: { region, language },
  req,
}) => {
  const headerTopMenuData = await fetchHeaderTopMenuData(
    getLocale(region, language),
    'isotopmenu'
  );
  const headerMenuData = await fetchHeaderMenuData(
    getLocale(region, language),
    'isomainmenu'
  );
  const footerData = await fetchFooterData(
    getLocale(region, language),
    'isofooter'
  );
  // const pageData = await fetchDetails(region, language);

  return {
    props: {
      headerTopMenuData: headerTopMenuData?.data || null,
      headerMenuData: headerMenuData?.data || null,
      region: region,
      language: language,
      footerData: footerData?.data || null,
      // pageData: pageData || null,
      isPreview: preview ? true : false,
    },
  };
};

export default ProgramDetails;
